/** ISO-BMFF brands that identify HEIC/HEIF containers. */
const HEIC_FTYP_BRANDS = new Set(['heic', 'heix', 'hevc', 'heif', 'mif1', 'msf1']);

const heicSniffCache = new WeakMap();

/**
 * Reads the first bytes of a file and checks for a HEIC/HEIF ftyp brand.
 * Results are cached per File object instance.
 *
 * @param {File|Blob} file
 * @returns {Promise<boolean>}
 */
export const sniffHeicFromFile = async (file) => {
  if (!file || typeof file.slice !== 'function') {
    return false;
  }

  if (heicSniffCache.has(file)) {
    return heicSniffCache.get(file);
  }

  const promise = (async () => {
    try {
      const header = await file.slice(0, 12).arrayBuffer();
      if (header.byteLength < 12) {
        return false;
      }

      const bytes = new Uint8Array(header);
      const boxType = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
      if (boxType !== 'ftyp') {
        return false;
      }

      const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]).toLowerCase();
      return HEIC_FTYP_BRANDS.has(brand);
    } catch {
      return false;
    }
  })();

  heicSniffCache.set(file, promise);
  return promise;
};

/**
 * Determines whether a file should be treated as HEIC/HEIF for preview and upload routing.
 * Combines MIME/extension checks with binary sniffing for mislabelled Android files.
 *
 * @param {File|Blob} file
 * @param {(file: File|Blob) => boolean} isHeicByMetadata
 * @returns {Promise<boolean>}
 */
export const resolveIsHeicFile = async (file, isHeicByMetadata) => {
  if (!file) {
    return false;
  }

  if (isHeicByMetadata(file)) {
    return true;
  }

  return sniffHeicFromFile(file);
};
